// components/DegreeAudit/DegreeRequirementsHeader.tsx

import { memo, useState, useRef, useMemo, type ChangeEvent } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { ChevronDown, ChevronRight } from 'lucide-react';
import CreditProgressBar from './Progress/CreditProgressBar';
import RequirementNode, { type RequirementNodeData } from './RequirementNode';
import { useCreditUnit, useInstitutionConfig } from '../../contexts/InstitutionConfigContext';
import { isAutoManagedProgram, isGenEdProgram } from '@/utils/programClassification';

interface ProgramSummary {
  program?: string;
  program_key?: string;
  module?: string;
  degree_type?: string;
  [key: string]: unknown;
}

interface UniversityRequirementsProgram {
  requirements?: RequirementNodeData[];
  [key: string]: unknown;
}

export interface HierarchicalSummaryData {
  majors?: ProgramSummary[];
  minors?: ProgramSummary[];
  citations?: ProgramSummary[];
  credits_completed?: number;
  credits_in_progress?: number;
  credits_planned?: number;
  credits_suggested?: number;
  [key: string]: unknown;
}

export interface DegreeRequirementsHeaderProps {
  hierarchicalData: HierarchicalSummaryData | null | undefined;
  academicPlan?: { student_name?: string; credit_target?: number; [key: string]: unknown } | null;
  onOpenManageModal?: () => void;
  onRefreshData?: () => void;
  onCreditTargetChange?: (target: number) => void;
  creditTarget?: number;
  universityRequirements?: UniversityRequirementsProgram | null;
  expandedNodes?: Record<string, boolean>;
  toggleNode?: (path: string) => void;
}

const DegreeRequirementsHeader = memo(
  ({
    hierarchicalData,
    academicPlan,
    onOpenManageModal,
    onCreditTargetChange,
    creditTarget,
    universityRequirements,
    expandedNodes,
    toggleNode,
  }: DegreeRequirementsHeaderProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    // W49 A3: institution's graduation target (120 four-year, 60/90 CC)
    // and the extended dual-degree target (base * 1.25, matching the
    // backend's allowed_credit_targets — UMD {120, 150} exactly).
    const { config } = useInstitutionConfig();
    const creditUnit = useCreditUnit();
    const baseCredits = config.graduationCredits;
    const extendedCredits = Math.round(baseCredits * 1.25);

    const majors = hierarchicalData?.majors || [];
    const minors = hierarchicalData?.minors || [];
    const citations = hierarchicalData?.citations || [];

    const isGenEd = (p: ProgramSummary): boolean => isGenEdProgram(p);
    const isAutoManaged = (p: ProgramSummary): boolean => isAutoManagedProgram(p);

    const majorCount = majors.filter((m) => !isAutoManaged(m)).length;
    const minorCount = minors.length;
    const citationCount = citations.length;
    const hasGenEd = majors.some(isGenEd);

    const effectiveCreditTarget =
      creditTarget ?? academicPlan?.credit_target ?? baseCredits;

    const buildSummaryText = (): string => {
      const parts: string[] = [];
      if (majorCount > 0) parts.push(`${majorCount} Major${majorCount > 1 ? 's' : ''}`);
      if (minorCount > 0) parts.push(`${minorCount} Minor${minorCount > 1 ? 's' : ''}`);
      if (citationCount > 0)
        parts.push(`${citationCount} Citation${citationCount > 1 ? 's' : ''}`);
      if (hasGenEd) parts.push('General Education');

      return parts.length > 0 ? `(${parts.join(', ')})` : '';
    };

    const degreeCredits = useMemo(() => {
      if (!hierarchicalData) return null;
      return {
        credits_completed: hierarchicalData.credits_completed || 0,
        credits_in_progress: hierarchicalData.credits_in_progress || 0,
        credits_planned: hierarchicalData.credits_planned || 0,
        credits_suggested: hierarchicalData.credits_suggested || 0,
      };
    }, [hierarchicalData]);

    useClickOutside(dropdownRef, () => setShowDropdown(false));

    const renderCreditLabel = (): JSX.Element => {
      if (majorCount <= 1) {
        return (
          <p className="text-xs text-gray-500 mt-1">
            {effectiveCreditTarget} {creditUnit.plural} required for degree
          </p>
        );
      }

      return (
        <select
          className="text-xs text-gray-500 mt-1 bg-transparent border-none cursor-pointer hover:text-gray-700 focus:ring-0 focus:outline-none p-0"
          value={effectiveCreditTarget}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
            e.stopPropagation();
            onCreditTargetChange?.(Number(e.target.value));
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {majorCount === 2 ? (
            <>
              <option value={baseCredits}>
                {baseCredits} {creditUnit.plural} required for double major — 1 diploma
              </option>
              <option value={extendedCredits}>
                {extendedCredits} {creditUnit.plural} required for dual degree — 2 diplomas
              </option>
            </>
          ) : (
            <>
              <option value={baseCredits}>
                {baseCredits} {creditUnit.plural} required for single diploma w/ multiple majors
              </option>
              <option value={extendedCredits}>
                {extendedCredits} {creditUnit.plural} required for multiple diplomas
              </option>
            </>
          )}
        </select>
      );
    };

    const univReqProgram = universityRequirements;
    const hasUnivReqs =
      !!univReqProgram &&
      !!univReqProgram.requirements &&
      univReqProgram.requirements.length > 0;

    return (
      <>
      {/* UI Tier 3.3b: column header sits unboxed on the ground; the card
          below holds the summary (one framing language across columns). */}
      <div className="flex items-center justify-between px-1 pb-1">
        <h1 className="text-lg font-semibold text-ink">Degree Requirements</h1>
        <div className="flex items-center gap-2">
          {hasUnivReqs && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              aria-expanded={isExpanded}
              aria-label={
                isExpanded
                  ? 'Hide university requirements'
                  : 'Show university requirements'
              }
              className="p-1.5 text-ink-3 hover:text-ink rounded-control hover:bg-surface-sunken transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronRight className="w-5 h-5" />
              )}
            </button>
          )}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              className="p-1.5 text-ink-3 hover:text-ink rounded-control hover:bg-surface-sunken transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setShowDropdown(!showDropdown);
              }}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zM12 13a1 1 0 110-2 1 1 0 010 2zM12 20a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </button>

            {showDropdown && (
              <div
                className="absolute right-0 mt-2 w-48 bg-surface-card rounded-card shadow-lg border border-line-soft py-1 z-50"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    onOpenManageModal?.();
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-ink hover:bg-surface-sunken transition-colors"
                >
                  Manage programs
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="bg-surface-card rounded-card shadow-sm border border-line-soft overflow-visible">
        <div
          className={`p-3 md:p-4 ${hasUnivReqs ? 'cursor-pointer hover:bg-surface-sunken/60' : ''} transition-colors`}
          onClick={hasUnivReqs ? () => setIsExpanded(!isExpanded) : undefined}
          title={
            hasUnivReqs
              ? isExpanded
                ? 'Collapse university requirements'
                : 'Expand university requirements'
              : undefined
          }
        >
          <p className="text-sm text-ink-2">
            Academic progress for {academicPlan?.student_name || 'student'} {buildSummaryText()}
          </p>

          {majorCount > 0 && degreeCredits && (
            <div className="mt-3" onClick={(e) => e.stopPropagation()}>
              <CreditProgressBar
                majorData={degreeCredits}
                totalCredits={effectiveCreditTarget}
              />
              {renderCreditLabel()}
            </div>
          )}
        </div>

        {isExpanded && hasUnivReqs && univReqProgram?.requirements && (
          <div className="border-t border-line-soft bg-surface-sunken/50 p-3 md:p-4">
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-slate-600 mb-2">University Requirements</h4>
              {univReqProgram.requirements.map((requirement, reqIndex) => (
                <RequirementNode
                  key={`univ-req-${reqIndex}`}
                  requirement={requirement}
                  path={`univ-req-${reqIndex}`}
                  expandedNodes={expandedNodes || {}}
                  toggleNode={toggleNode || (() => {})}
                  majorData={univReqProgram}
                  programType="university_requirements"
                  programName="University Requirements"
                  isTopLevel={true}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      </>
    );
  }
);

DegreeRequirementsHeader.displayName = 'DegreeRequirementsHeader';

export default DegreeRequirementsHeader;
